/**
 * Lazy loading dei componenti per code splitting e performance
 * Riduce il bundle iniziale caricando componenti solo quando necessari
 */

import React, { Suspense } from 'react';
import { CircularProgress, Box } from '@mui/material';

/**
 * Componente di loading standardizzato
 */
const LoadingFallback = ({ message = 'Caricamento...' }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '200px',
      gap: 2
    }}
  >
    <CircularProgress />
    <span>{message}</span>
  </Box>
);

/**
 * HOC per wrappare componenti lazy con Suspense
 */
export const withLazyLoading = (LazyComponent, fallbackMessage) => {
  return React.memo((props) => (
    <Suspense fallback={<LoadingFallback message={fallbackMessage} />}>
      <LazyComponent {...props} />
    </Suspense>
  ));
};

// =================
// PAGINE PRINCIPALI
// =================

/**
 * Dashboard Page - Componente pesante con charts
 */
export const LazyDashboardPage = React.lazy(() => 
  import('../pages/DashboardPage').then(module => {
    return module;
  })
);

/**
 * Table Page - Lista anziani con paginazione
 */
export const LazyTablePage = React.lazy(() => 
  import('../pages/TablePage').then(module => {
    return module;
  })
);

/**
 * Elderly Description Page - Dettaglio anziano
 */
export const LazyElderlyDescriptionPage = React.lazy(() => 
  import('../pages/ElderlyDescriptionPage').then(module => {
    return module;
  })
);

/**
 * Elderly Add Page - Form aggiunta anziano
 */
export const LazyElderlyAddPage = React.lazy(() => 
  import('../pages/ElderlyAddPage').then(module => {
    return module;
  })
);

/**
 * Edit Elderly Page - Form modifica anziano
 */
export const LazyEditElderlyPage = React.lazy(() => 
  import('../pages/EditElderlyPage').then(module => {
    return module;
  })
);

/**
 * Settings Page - Configurazioni
 */
export const LazySettingsPage = React.lazy(() => 
  import('../pages/SettingsPage').then(module => {
    return module;
  })
);

/**
 * Calendar Page - Calendario appuntamenti
 */
export const LazyCalendarPage = React.lazy(() => 
  import('../pages/CalendarPage').then(module => {
    return module;
  })
);

// =================
// PAGINE SPECIFICHE
// =================

/**
 * Doctor Page - Gestione dottori
 */
export const LazyDoctorPage = React.lazy(() => 
  import('../pages/DoctorPage').then(module => {
    return module;
  })
);

/**
 * Manage Doctors Page - Lista dottori
 */
export const LazyManageDoctorsPage = React.lazy(() => 
  import('../pages/ManageDoctorsPage').then(module => {
    return module;
  })
);

/**
 * Manage Rooms Page - Gestione camere
 */
export const LazyManageRoomsPage = React.lazy(() => 
  import('../pages/ManageRoomsPage').then(module => {
    return module;
  })
);

/**
 * Family Members Page - Familiari
 */
export const LazyFamilyMembersPage = React.lazy(() => 
  import('../pages/FamilyMembersPage').then(module => {
    return module;
  })
);

/**
 * Elderly Emergency Stats Page - Statistiche chiamate emergenza
 */
export const LazyElderlyEmergencyStatsPage = React.lazy(() => 
  import('../pages/ElderlyEmergencyStatsPage').then(module => {
    return module;
  })
);

/**
 * Therapy Page - Gestione terapie
 */
export const LazyTherapyPage = React.lazy(() => 
  import('../pages/TherapyPage').then(module => {
    return module;
  })
);

/**
 * Nurse Page - Gestione infermieri
 */
export const LazyNursePage = React.lazy(() => 
  import('../pages/NursePage').then(module => {
    return module;
  })
);

/**
 * Shelter Page - Gestione ricoveri
 */
export const LazyShelterPage = React.lazy(() => 
  import('../pages/ShelterPage').then(module => {
    return module;
  })
);

// =================
// PAGINE DISPLAY
// =================

/**
 * TV Display Page - Display per TV
 */
export const LazyTvDisplayPage = React.lazy(() => 
  import('../pages/TvDisplayPage').then(module => {
    return module;
  })
);

/**
 * TV Display Legacy Page - Display legacy
 */
export const LazyTvDisplayPageLegacy = React.lazy(() => 
  import('../pages/TvDisplayPageLegacy').then(module => {
    return module;
  })
);

// =================
// PAGINE SENSORI
// =================

/**
 * Sensors Page - Lista sensori
 */
export const LazySensorsPage = React.lazy(() => 
  import('../pages/SensorsPage').then(module => {
    return module;
  })
);

/**
 * Sensor Chart Page - Grafici sensori
 */
export const LazySensorThChartPage = React.lazy(() => 
  import('../pages/SensorThChartPage').then(module => {
    return module;
  })
);

// =================
// COMPONENTI PESANTI
// =================

/**
 * Alerts Widget - Widget alert
 */
export const LazyAlertsWidget = React.lazy(() => 
  import('../components/AlertsWidget').then(module => {
    return module;
  })
);

/**
 * Carousel Configuration - Configurazione carousel
 */
export const LazyCarouselConfiguration = React.lazy(() => 
  import('../components/CarouselConfiguration').then(module => {
    return module;
  })
);

/**
 * Print Button - Componente stampa (PDF pesante)
 */
export const LazyPrintButton = React.lazy(() => 
  import('../components/PrintButton').then(module => {
    return module;
  })
);

// =================
// WRAPPER STANDARDIZZATI
// =================

/**
 * Dashboard con lazy loading
 */
export const DashboardPageLazy = withLazyLoading(
  LazyDashboardPage, 
  'Caricamento dashboard...'
);

/**
 * Tabella con lazy loading
 */
export const TablePageLazy = withLazyLoading(
  LazyTablePage, 
  'Caricamento lista anziani...'
);

/**
 * Dettaglio anziano con lazy loading
 */
export const ElderlyDescriptionPageLazy = withLazyLoading(
  LazyElderlyDescriptionPage, 
  'Caricamento dettagli anziano...'
);

/**
 * Form aggiunta con lazy loading
 */
export const ElderlyAddPageLazy = withLazyLoading(
  LazyElderlyAddPage, 
  'Caricamento form...'
);

/**
 * Form modifica con lazy loading
 */
export const EditElderlyPageLazy = withLazyLoading(
  LazyEditElderlyPage, 
  'Caricamento form modifica...'
);

/**
 * Impostazioni con lazy loading
 */
export const SettingsPageLazy = withLazyLoading(
  LazySettingsPage, 
  'Caricamento impostazioni...'
);

/**
 * Calendario con lazy loading
 */
export const CalendarPageLazy = withLazyLoading(
  LazyCalendarPage, 
  'Caricamento calendario...'
);

/**
 * Dottori con lazy loading
 */
export const DoctorPageLazy = withLazyLoading(
  LazyDoctorPage, 
  'Caricamento gestione dottori...'
);

/**
 * Lista dottori con lazy loading
 */
export const ManageDoctorsPageLazy = withLazyLoading(
  LazyManageDoctorsPage, 
  'Caricamento lista dottori...'
);

/**
 * Gestione camere con lazy loading
 */
export const ManageRoomsPageLazy = withLazyLoading(
  LazyManageRoomsPage, 
  'Caricamento gestione camere...'
);

/**
 * Familiari con lazy loading
 */
export const FamilyMembersPageLazy = withLazyLoading(
  LazyFamilyMembersPage, 
  'Caricamento familiari...'
);

/**
 * Statistiche emergenza con lazy loading
 */
export const ElderlyEmergencyStatsPageLazy = withLazyLoading(
  LazyElderlyEmergencyStatsPage, 
  'Caricamento statistiche...'
);

/**
 * Terapie con lazy loading
 */
export const TherapyPageLazy = withLazyLoading(
  LazyTherapyPage, 
  'Caricamento terapie...'
);

/**
 * Infermieri con lazy loading
 */
export const NursePageLazy = withLazyLoading(
  LazyNursePage, 
  'Caricamento infermieri...'
);

/**
 * Ricoveri con lazy loading
 */
export const ShelterPageLazy = withLazyLoading(
  LazyShelterPage, 
  'Caricamento ricoveri...'
);

/**
 * Display TV con lazy loading
 */
export const TvDisplayPageLazy = withLazyLoading(
  LazyTvDisplayPage, 
  'Caricamento display TV...'
);

/**
 * Sensori con lazy loading
 */
export const SensorsPageLazy = withLazyLoading(
  LazySensorsPage, 
  'Caricamento sensori...'
);

/**
 * Grafici sensori con lazy loading
 */
export const SensorChartPageLazy = withLazyLoading(
  LazySensorThChartPage, 
  'Caricamento grafici...'
);

/**
 * Widget alert con lazy loading
 */
export const AlertsWidgetLazy = withLazyLoading(
  LazyAlertsWidget, 
  'Caricamento alert...'
);

/**
 * Configurazione carousel con lazy loading
 */
export const CarouselConfigurationLazy = withLazyLoading(
  LazyCarouselConfiguration, 
  'Caricamento configurazione...'
);

/**
 * Stampa con lazy loading
 */
export const PrintButtonLazy = withLazyLoading(
  LazyPrintButton, 
  'Caricamento stampa...'
);

// =================
// EXPORT DEFAULT
// =================

export default {
  // Pagine principali
  DashboardPageLazy,
  TablePageLazy,
  ElderlyDescriptionPageLazy,
  ElderlyAddPageLazy,
  EditElderlyPageLazy,
  SettingsPageLazy,
  CalendarPageLazy,
  
  // Pagine specifiche
  DoctorPageLazy,
  ManageDoctorsPageLazy,
  ManageRoomsPageLazy,
  FamilyMembersPageLazy,
  TherapyPageLazy,
  NursePageLazy,
  ShelterPageLazy,
  
  // Display
  TvDisplayPageLazy,
  SensorsPageLazy,
  SensorChartPageLazy,
  
  // Componenti
  AlertsWidgetLazy,
  CarouselConfigurationLazy,
  PrintButtonLazy,
  
  // Utilities
  withLazyLoading,
  LoadingFallback
};